/**
 * Shared windowing primitive for the DOM view surfaces (table / matrix / grid,
 * and later index). Wraps `@tanstack/react-virtual`'s `useVirtualizer` into a
 * SPACER-SLICE model: a surface renders only the visible slice of real
 * `<tr>`/`<td>`, plus spacer rows/cells whose `before`/`after` px absorb the
 * off-screen extent — so the native `<table>` layout, sticky headers, and full
 * scroll height are all preserved (no table→CSS-grid rewrite).
 *
 * Below {@link WINDOW_THRESHOLD} items the hook bypasses windowing entirely
 * (renders the full list, no spacers) — tiny tables stay simple and exact. The
 * surface's LAYOUT mode (`table-layout: fixed` + measured widths) is constant
 * either regime, so engaging windowing never swaps layout mid-scroll.
 *
 * The slice math (`fullSlice` / `windowSlice`) is pure and unit-tested; the hook
 * is the thin React seam over `useVirtualizer` that feeds it live virtual items.
 */
import { useVirtualizer, type VirtualizerOptions } from "@tanstack/react-virtual";
import type { RefObject } from "react";

/** Item count at/below which a surface renders in full (no windowing). */
export const WINDOW_THRESHOLD = 50;
/** Rows/cols kept mounted beyond the viewport, so scroll never flashes blank. */
export const OVERSCAN = 8;

/** One axis's render plan: which absolute indices to draw + the spacer sizes. */
export interface Slice {
  /** Absolute indices to render, in order. */
  readonly indices: number[];
  /** Spacer size (px) before the first rendered index. */
  readonly before: number;
  /** Spacer size (px) after the last rendered index. */
  readonly after: number;
}

/** Every index, no spacers — the sub-threshold (render-all) plan. */
export function fullSlice(count: number): Slice {
  const indices: number[] = [];
  for (let i = 0; i < count; i++) indices.push(i);
  return { indices, before: 0, after: 0 };
}

/** The windowed plan from live virtual items + the axis total size. Spacers are
 *  clamped ≥0 so a stale/rounding total never yields a negative spacer. */
export function windowSlice(
  items: readonly { readonly index: number; readonly start: number; readonly end: number }[],
  total: number,
): Slice {
  if (items.length === 0) return { indices: [], before: 0, after: Math.max(0, total) };
  const first = items[0]!;
  const last = items[items.length - 1]!;
  return {
    indices: items.map((i) => i.index),
    before: Math.max(0, first.start),
    after: Math.max(0, total - last.end),
  };
}

export interface WindowAxis extends Slice {
  /** True once the axis exceeds the threshold and is genuinely windowed. */
  readonly windowed: boolean;
  /** Attach to each rendered element's `ref` for DYNAMIC sizing (edited rows
   *  auto-grow). No-op when `dynamic` is off (fixed `estimateSize`). The element
   *  must also carry `data-index={absoluteIndex}` for the measurement to bind. */
  readonly measureElement: (el: Element | null) => void;
}

export interface WindowOpts {
  /** Horizontal axis (matrix columns). Default vertical (rows). */
  readonly horizontal?: boolean;
  /** Initial per-item size guess (px). Fixed size when `dynamic` is off. */
  readonly estimateSize: number;
  /** Measure each element's true size (rows that wrap/grow). Off = uniform. */
  readonly dynamic?: boolean;
  /** Test-only seam: inject deterministic virtualizer observers (jsdom has no
   *  layout, so the real DOM observers report a 0×0 viewport). Production leaves
   *  this undefined and uses `@tanstack/react-virtual`'s real DOM observers. */
  readonly testOverrides?: Partial<
    Pick<
      VirtualizerOptions<HTMLElement, Element>,
      "observeElementRect" | "observeElementOffset" | "scrollToFn"
    >
  >;
}

const NOOP = (_el: Element | null): void => {};

/**
 * Window one axis of a surface over its scroll parent. Always calls
 * `useVirtualizer` (hooks are unconditional); returns the full-render plan below
 * the threshold and the spacer-slice plan above it.
 */
export function useWindow(
  scrollRef: RefObject<HTMLElement | null>,
  count: number,
  opts: WindowOpts,
): WindowAxis {
  const virt = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => opts.estimateSize,
    horizontal: opts.horizontal ?? false,
    overscan: OVERSCAN,
    ...opts.testOverrides,
  });
  if (count <= WINDOW_THRESHOLD) {
    return { ...fullSlice(count), windowed: false, measureElement: NOOP };
  }
  const slice = windowSlice(virt.getVirtualItems(), virt.getTotalSize());
  return { ...slice, windowed: true, measureElement: opts.dynamic ? virt.measureElement : NOOP };
}
